import { Box, Stack, SxProps, Theme } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { Card } from '@/components/layout/Card/Card';
import { FloatingMenuContainer } from '@/components/layout/FloatingMenuContainer';
import { ButtonTab, Tabs } from '@/components/navigation/NavigationTabs';
import { EntityDocumentViewer_FilePageFragment } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { PDFSource, PDFViewerLazy } from '@/modules/pdf/PDFViewer';
import { useAnnotatedPDFViewerContext } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';
import type { DocumentChromeOverrides } from '@/modules/pdf/PDFViewer/DocumentChrome';
import { convertGraphQLFilePages } from '@/modules/pdf/utils/filePageUtils';
import { COLORS } from '@/styles/tokens/colors';

import { DocumentSearchBar } from '../../pdf/DocumentSearch/DocumentSearchBar';
import { DocumentDetailsViewOptions } from '../documents.constants';
import { SelectableDocument } from '../ManageDocumentAmendments/types';
import { DocumentVersionSelector } from './DocumentVersionSelector/DocumentVersionSelector';

interface DocumentViewerWithSearchProps {
  loading?: boolean;
  documentId: string;
  source: PDFSource | null;
  activeTab?: DocumentDetailsViewOptions;
  setActiveTab?: (value: DocumentDetailsViewOptions) => void;
  sx?: SxProps<Theme>;
  documentChromeProps?: DocumentChromeOverrides;
  filePages: EntityDocumentViewer_FilePageFragment[];
  associatedDocuments?: SelectableDocument[];
  onSelectDocument?: (documentId: string) => void;
  onOpenChatWithSummary?: () => void;
  /** Forwarded to PDFViewer to control how many pixels are subtracted from the container width. */
  pageWidthOffset?: number;
}

export function DocumentViewerWithSearch({
  loading,
  documentId,
  source,
  activeTab,
  setActiveTab,
  sx,
  documentChromeProps,
  filePages,
  associatedDocuments,
  onSelectDocument,
  onOpenChatWithSummary,
  pageWidthOffset,
}: DocumentViewerWithSearchProps) {
  const normalizedFilePages = convertGraphQLFilePages(filePages);

  // Get annotations and activeAnnotationIndex from context
  const { annotations, activeAnnotationIndex } = useAnnotatedPDFViewerContext();

  return (
    <Stack flex={1} height="100%" overflow="auto">
      <Card
        data-name="DocumentViewerWithSearch-container"
        variant="outlined"
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          px: 2,
          position: 'relative',
          backgroundColor: COLORS.GRAY[50],
          ...sx,
        }}
      >
        <Box sx={{ backgroundColor: 'white', pt: 2 }}>
          {activeTab && setActiveTab && (
            <Tabs>
              <ButtonTab
                display="Summary"
                isActive={activeTab === 'summary'}
                onClick={() => setActiveTab('summary')}
              />
              <ButtonTab
                display="Document"
                isActive={activeTab === 'document'}
                onClick={() => setActiveTab('document')}
              />
            </Tabs>
          )}
        </Box>
        <Box sx={{ overflowY: 'auto', position: 'relative', height: '100%' }}>
          {/* Overlay the openChatWithSummary button on the PDF viewer
          to mirror the summary header behavior */}
          <FloatingMenuContainer offset={{ top: 16, left: 0, right: 8 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                {associatedDocuments &&
                  associatedDocuments.length > 1 &&
                  onSelectDocument && (
                    <DocumentVersionSelector
                      documents={associatedDocuments}
                      activeDocumentId={documentId}
                      onSelectDocument={onSelectDocument}
                      sx={{ ml: 2 }}
                    />
                  )}
                {onOpenChatWithSummary && (
                  <Button
                    size="sm"
                    variant="secondary"
                    startIcon={Stars01Icon}
                    onClick={onOpenChatWithSummary}
                    iconProps={{ color: COLORS.TEAL['600'], size: 16 }}
                  >
                    Chat
                  </Button>
                )}
              </Stack>
            </Stack>
          </FloatingMenuContainer>
          <PDFViewerLazy
            loading={loading}
            documentId={documentId}
            source={source}
            activeAnnotationIdx={activeAnnotationIndex}
            annotations={annotations}
            documentChromeProps={documentChromeProps}
            filePages={normalizedFilePages}
            pageWidthOffset={pageWidthOffset}
            slots={{
              DocumentFooter: DocumentSearchBar,
            }}
          />
        </Box>
      </Card>
    </Stack>
  );
}
